import * as React from 'react';
import classNames from 'classnames';
import SvgSprite from 'src/react/components/html-elements/SvgSprite';
import { AvatarType } from 'ui/avatar/types';
import { AvatarBadge } from 'ui/avatar/AvatarBadge';
import { TextColor } from '@collaborator/npm_modules/dist/css/text-color';
import { BGColor } from '@collaborator/npm_modules/dist/css/bg-color';
import { forwardRef, ReactElement } from 'react';
import { PopoverStickOnHoverPropTypes } from 'components/popover/PopoverStickOnHoverPropTypes';
import { PopoverStickOnHover } from 'components/popover/PopoverStickOnHover';

type PropTypes = AvatarType & {
  additionalClassName?: string;
  popoverProps?: PopoverStickOnHoverPropTypes,
};

const Avatar = forwardRef<HTMLDivElement, PropTypes>(({
  image,
  icon,
  textColor,
  bgColor,
  badge = {},
  sizeXxs,
  sizeXs,
  sizeSm,
  sizeLg,
  sizeXl,
  bordered,
  borderGreen,
  additionalClassName,
  popoverProps,
}, forwardedRef) => {
  const iconTextColorDefault = TextColor.textPale;
  const iconBGColorDefault = BGColor.light;
  const iconSizeDefault = 24;

  const renderContent = () => {
    if (icon) {
      const { id, size = iconSizeDefault } = icon;
      return <SvgSprite id={id} size={size} />;
    }

    return <img src={image} className="avatar-img" />;
  };

  const wrapPopover = (component): ReactElement => {
    if (!popoverProps) {
      return component;
    }

    return (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <PopoverStickOnHover {...popoverProps}>
        {component}
      </PopoverStickOnHover>
    );
  };

  const component = (
    <div
      className={classNames(
        'avatar',
        { 'avatar-with-icon': icon },
        icon ? textColor || iconTextColorDefault : null,
        icon ? bgColor || iconBGColorDefault : null,
        { 'avatar-xxs': sizeXxs },
        { 'avatar-xs': sizeXs },
        { 'avatar-sm': sizeSm },
        { 'avatar-lg': sizeLg },
        { 'avatar-xl': sizeXl },
        { 'avatar-bordered': bordered },
        { 'avatar-border-green': borderGreen },
        additionalClassName,
      )}
      ref={forwardedRef}
    >
      {renderContent()}
      <AvatarBadge icon={badge.icon} text={badge.text} style={badge.style} />
    </div>
  );

  return wrapPopover(component);
});

export { Avatar };
